import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ColumnsMenuItem {
  id: string;
  label: string;
  shown: boolean;
  locked?: boolean;
}

interface ColumnsMenuProps {
  // Every column in the table's current order, hidden ones included.
  items: ColumnsMenuItem[];
  onToggle: (id: string) => void;
  onMove: (dragged: string, target: string) => void;
  onMoveBy: (id: string, step: -1 | 1) => void;
  onReset: () => void;
  canReset: boolean;
}

// Not the header drag type: a row from this list dropped on the table, or a
// header dropped here, is not something either side knows what to do with.
const ROW_DRAG_TYPE = "application/x-wlx-column-row";

// "Kolommen" in a table's toolbar (design-system data.html, 07): tick the
// columns you want, drag a row to move its column. The same moves work in the
// table itself — this is where the hidden ones are, and the keyboard way.
export function ColumnsMenu(props: ColumnsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null) =>
      target instanceof Node && (popoverRef.current?.contains(target) || triggerRef.current?.contains(target));
    const onPointerDown = (e: globalThis.PointerEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    // Tabbing out of the list closes it too; clicking inside keeps the focus
    // inside, because the popover itself can take it.
    const onFocusIn = (e: FocusEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open]);

  return (
    <span className="wl-columns">
      <button
        ref={triggerRef}
        type="button"
        className={`wl-btn wl-btn--sm ${open ? "wl-btn--secondary" : "wl-btn--ghost"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t("columns.buttonHint")}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" />
        </svg>
        {t("columns.button")}
      </button>
      {open && (
        <ColumnsPopover
          {...props}
          popoverRef={popoverRef}
          onClose={() => {
            setOpen(false);
            triggerRef.current?.focus();
          }}
        />
      )}
    </span>
  );
}

function ColumnsPopover({
  items,
  onToggle,
  onMove,
  onMoveBy,
  onReset,
  canReset,
  popoverRef,
  onClose,
}: ColumnsMenuProps & { popoverRef: React.RefObject<HTMLDivElement | null>; onClose: () => void }) {
  const { t } = useTranslation();
  const hintId = useId();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // The column moved with the keyboard. Moving a row down re-inserts its DOM
  // node, which takes the focus away from its checkbox; this puts it back.
  const refocus = useRef<string | null>(null);

  // Measured before the first paint, so it never shows running off the screen
  // and then jumping back.
  const [alignEnd, setAlignEnd] = useState(false);
  const [alignUp, setAlignUp] = useState(false);
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth) setAlignEnd(true);
    // At the foot of a pane (step 1's footer, 19-10-2026) it ran off the bottom of the screen and looked dead.
    if (rect.bottom > window.innerHeight) setAlignUp(true);
    el.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, [popoverRef]);

  useLayoutEffect(() => {
    const id = refocus.current;
    if (!id) return;
    refocus.current = null;
    popoverRef.current?.querySelector<HTMLInputElement>(`input[data-column="${CSS.escape(id)}"]`)?.focus();
  });

  const fromIndex = dragging ? items.findIndex((i) => i.id === dragging) : -1;

  return (
    <div
      ref={popoverRef}
      className={`wl-popover wl-columns__popover${alignEnd ? " wl-columns__popover--end" : ""}${alignUp ? " wl-columns__popover--up" : ""}`}
      role="dialog"
      aria-label={t("columns.title")}
      tabIndex={-1}
      onKeyDown={(e) => {
        // Nothing typed here reaches the screen's own shortcuts.
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className="wl-menu__header">{t("columns.title")}</div>
      <ul className="wl-columns__list">
        {items.map((item, index) => {
          const side = dragging && over === item.id && fromIndex !== index ? (fromIndex < index ? "after" : "before") : null;
          return (
            <li
              key={item.id}
              className={`wl-columns__item${dragging === item.id ? " is-dragging" : ""}${side ? ` is-drop-${side}` : ""}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(ROW_DRAG_TYPE, item.id);
                e.dataTransfer.effectAllowed = "move";
                setDragging(item.id);
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onDragOver={(e) => {
                if (!dragging || dragging === item.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (over !== item.id) setOver(item.id);
              }}
              onDragLeave={(e) => {
                if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
                setOver((v) => (v === item.id ? null : v));
              }}
              onDrop={(e) => {
                if (!dragging) return;
                e.preventDefault();
                onMove(dragging, item.id);
                setDragging(null);
                setOver(null);
              }}
            >
              <span className="wl-columns__grip" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" />
                  <circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" />
                </svg>
              </span>
              <label className="wl-columns__label">
                {/* A locked column stays focusable (aria-disabled, not
                    disabled) so it can still be moved with the keyboard. */}
                <input
                  type="checkbox"
                  data-column={item.id}
                  checked={item.shown}
                  aria-disabled={item.locked || undefined}
                  aria-describedby={hintId}
                  onChange={() => {
                    if (!item.locked) onToggle(item.id);
                  }}
                  onKeyDown={(e) => {
                    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
                    e.preventDefault();
                    refocus.current = item.id;
                    onMoveBy(item.id, e.key === "ArrowUp" ? -1 : 1);
                  }}
                />
                <span>{item.label}</span>
                {item.locked && <span className="wl-columns__note">{t("columns.alwaysShown")}</span>}
              </label>
            </li>
          );
        })}
      </ul>
      <p id={hintId} className="wl-columns__hint">
        {t("columns.hint")}
      </p>
      <div className="wl-menu__divider" role="separator" />
      <button
        type="button"
        className="wl-menu__item"
        aria-disabled={!canReset || undefined}
        onClick={() => {
          if (canReset) onReset();
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
        </svg>
        {t("columns.reset")}
      </button>
    </div>
  );
}
